import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const ChangePasswordPage: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { member } = useAuth();

  const validateForm = (): boolean => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return false;
    }

    if (newPassword.length < 4) {
      setError('New password must be at least 4 characters');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match');
      return false;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const res = await apiCall('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      if (res.ok) {
        setSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');

        // Redirect after 2 seconds
        setTimeout(() => {
          navigate('/gig');
        }, 2000);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to change password');
      }
    } catch (err: any) {
      setError('Failed to connect to server. Please try again.');
      console.error('Change password error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      maxWidth: 500,
      margin: '40px auto',
      padding: 32,
      background: '#28190f',
      borderRadius: 8,
      border: '1px solid #5a3a1a',
      boxShadow: '0 4px 16px rgba(0,0,0,0.4)'
    }}>
      <h2 style={{ marginBottom: '24px', color: '#ffd700' }}>Change Password</h2>

      {success && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: 'rgba(46,204,113,0.15)',
          color: '#2ecc71',
          borderRadius: '4px',
          border: '1px solid rgba(46,204,113,0.3)'
        }}>
          Password changed successfully! Redirecting...
        </div>
      )}

      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: 'rgba(231,76,60,0.15)',
          color: '#e74c3c',
          borderRadius: '4px',
          border: '1px solid rgba(231,76,60,0.3)'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#c8a060' }}>
            Current Password
          </label>
          <input
            type="password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            placeholder="Enter current password"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '14px',
              borderRadius: '4px',
              border: '1px solid #5a3a1a',
              backgroundColor: '#1a0f0a',
              color: '#f5deb3',
              boxSizing: 'border-box'
            }}
            disabled={loading || success}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#c8a060' }}>
            New Password
          </label>
          <input
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder="Enter new password (min 4 characters)"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '14px',
              borderRadius: '4px',
              border: '1px solid #5a3a1a',
              backgroundColor: '#1a0f0a',
              color: '#f5deb3',
              boxSizing: 'border-box'
            }}
            disabled={loading || success}
          />
          {newPassword.length > 0 && newPassword.length < 4 && (
            <p style={{ marginTop: '4px', fontSize: '12px', color: '#e74c3c' }}>
              Password must be at least 4 characters
            </p>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#c8a060' }}>
            Confirm New Password
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '14px',
              borderRadius: '4px',
              border: '1px solid #5a3a1a',
              backgroundColor: '#1a0f0a',
              color: '#f5deb3',
              boxSizing: 'border-box'
            }}
            disabled={loading || success}
          />
          {confirmPassword.length > 0 && newPassword !== confirmPassword && (
            <p style={{ marginTop: '4px', fontSize: '12px', color: '#e74c3c' }}>
              Passwords do not match
            </p>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
          type="submit"
          disabled={loading || success}
          style={{
            flex: 1,
            padding: '12px',
            background: loading || success ? '#4a3520' : 'linear-gradient(180deg, #c8850f 0%, #8b5a0a 100%)',
            color: loading || success ? '#8a7a6a' : 'white',
            border: loading || success ? '1px solid #5a3a1a' : '1px solid #d4960f',
            borderRadius: '4px',
            cursor: loading || success ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          {loading ? 'Changing Password...' : 'Change Password'}
        </button>
        <button
          type="button"
          onClick={() => navigate('/gig')}
          disabled={loading}
          style={{
            padding: '12px 24px',
            backgroundColor: '#5a3a1a',
            color: loading ? '#8a7a6a' : '#f5deb3',
            border: '1px solid #5a3a1a',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '16px'
          }}
        >
          Cancel
        </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordPage;
